import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usageApi, type UsageSnapshot, type UsageFetchLog } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TableShell, type ColumnSpec, type ListParams } from "@/components/TableShell";

const resultVariant: Record<string, "default" | "success" | "warning" | "destructive" | "muted"> = {
  success: "success",
  failed: "destructive",
  partial: "warning",
};

// doc/050 §4.1: 两个放大列表改为 server 分页 + 排序(默认 50/页,真 total)。
export function UsagePage() {
  const [logsParams, setLogsParams] = useState<ListParams>({ page: 1, page_size: 50 });
  const [snapshotsParams, setSnapshotsParams] = useState<ListParams>({ page: 1, page_size: 50 });
  const [resultFilter, setResultFilter] = useState<string>("all");

  const logsQ = useQuery({
    queryKey: ["usage-fetch-logs", logsParams.page, logsParams.page_size, logsParams.sort ?? "", logsParams.order ?? "", resultFilter],
    queryFn: () => usageApi.listFetchLogs({
      page: logsParams.page,
      page_size: logsParams.page_size,
      sort: logsParams.sort,
      order: logsParams.order,
      ...(resultFilter !== "all" ? { result: resultFilter } : {}),
    }),
  });
  const snapshotsQ = useQuery({
    queryKey: ["usage-snapshots", snapshotsParams.page, snapshotsParams.page_size, snapshotsParams.sort ?? "", snapshotsParams.order ?? ""],
    queryFn: () => usageApi.listSnapshots({
      page: snapshotsParams.page,
      page_size: snapshotsParams.page_size,
      sort: snapshotsParams.sort,
      order: snapshotsParams.order,
    }),
  });

  const logColumns: ColumnSpec<UsageFetchLog>[] = [
    { key: "occurred_at", label: "时间", sortable: true },
    { key: "site_id", label: "站点" },
    { key: "result", label: "结果", sortable: true },
    { key: "snapshot_count", label: "快照条数", sortable: true },
    { key: "source", label: "来源" },
    { key: "error", label: "错误" },
  ];
  const snapshotColumns: ColumnSpec<UsageSnapshot>[] = [
    { key: "sampled_at", label: "采样时间", sortable: true },
    { key: "distribution_task_id", label: "任务 ID" },
    { key: "used_quota", label: "已用配额", sortable: true },
    { key: "source", label: "来源" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">用量</h1>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => {
            snapshotsQ.refetch();
            logsQ.refetch();
          }}
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${snapshotsQ.isFetching || logsQ.isFetching ? "animate-spin" : ""}`} />
          手动刷新记录
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">按渠道手动刷新</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            外部用量不再定时采集。请到「所有 Key」使用"刷新本页用量",或点击单个渠道用量后的刷新按钮。
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">抓取记录</CardTitle>
        </CardHeader>
        <CardContent>
          <TableShell
            key={`logs|${resultFilter}`}
            mode="server"
            columns={logColumns}
            data={logsQ.data?.items}
            total={logsQ.data?.total}
            loading={logsQ.isLoading}
            emptyText="暂无抓取记录"
            defaultPageSize={50}
            onParamsChange={setLogsParams}
            filters={
              <div className="flex items-center gap-1 text-xs">
                {["all", "success", "failed", "partial"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setResultFilter(v)}
                    className={`rounded-md border px-2 py-1 transition-colors ${
                      resultFilter === v ? "border-primary bg-primary/5 text-primary" : "border-[#e4eaf1] text-muted-foreground hover:bg-muted/40"
                    }`}
                  >
                    {v === "all" ? "全部" : v}
                  </button>
                ))}
              </div>
            }
            rowKey={(l) => l.id}
            renderRow={(l) => (
              <tr className="border-b hover:bg-muted/30">
                <td className="py-2 text-xs">{new Date(l.occurred_at).toLocaleString()}</td>
                <td>#{l.site_id}</td>
                <td><Badge variant={resultVariant[l.result] ?? "muted"}>{l.result}</Badge></td>
                <td>{l.snapshot_count}</td>
                <td className="text-xs text-muted-foreground">{l.source}</td>
                <td className="max-w-xs truncate text-xs text-destructive">{l.error}</td>
              </tr>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">用量快照</CardTitle>
        </CardHeader>
        <CardContent>
          <TableShell
            mode="server"
            columns={snapshotColumns}
            data={snapshotsQ.data?.items}
            total={snapshotsQ.data?.total}
            loading={snapshotsQ.isLoading}
            emptyText="暂无快照"
            defaultPageSize={50}
            onParamsChange={setSnapshotsParams}
            rowKey={(s) => s.id}
            renderRow={(s) => (
              <tr className="border-b hover:bg-muted/30">
                <td className="py-2 text-xs">{new Date(s.sampled_at).toLocaleString()}</td>
                <td>#{s.distribution_task_id}</td>
                <td>{s.used_quota.toLocaleString()}</td>
                <td className="text-xs text-muted-foreground">{s.source}</td>
              </tr>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
